import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiGet, apiSend, resetCsrf, setAuthToken } from "./api";
import type { User } from "../types";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<{ user: User; csrfToken?: string; token?: string }>("/api/auth/me")
      .then((data) => {
        setUser(data.user);
        if (data.csrfToken) resetCsrf(data.csrfToken);
        if (data.token) setAuthToken(data.token);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(emailOrUsername, password) {
        const data = await apiSend<{ user: User; csrfToken?: string; token?: string }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ emailOrUsername, password })
        });
        setUser(data.user);
        if (data.token) setAuthToken(data.token);
        if (data.csrfToken) resetCsrf(data.csrfToken);
      },
      async logout() {
        try {
          await apiSend("/api/auth/logout", { method: "POST", body: "{}" });
        } catch {
          // ignore logout network errors
        }
        resetCsrf(null);
        setAuthToken(null);
        setUser(null);
      }
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
